import React, { useEffect, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  ENV_BANNER_HEIGHT_PX,
  normalizeAppEnvironment,
  resolveEnvironmentFromBuild,
  shouldShowEnvironmentBanner,
} from '../utils/environment';

export const NOTICE_TOP_OFFSET_PX = 8;
export const NOTICE_TOP_OFFSET_WITH_ENV_PX = NOTICE_TOP_OFFSET_PX + ENV_BANNER_HEIGHT_PX + 4;

function EnvironmentBanner() {
  const theme = useTheme();
  const { t } = useTranslation();
  const [tier, setTier] = useState(() => resolveEnvironmentFromBuild());

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/environment')
      .then((res) => {
        if (cancelled) return;
        const apiTier = normalizeAppEnvironment(res.data?.environment);
        if (apiTier) setTier(apiTier);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const show = shouldShowEnvironmentBanner(tier);
    const noticeTop = show ? `${NOTICE_TOP_OFFSET_WITH_ENV_PX}px` : `${NOTICE_TOP_OFFSET_PX}px`;
    document.documentElement.style.setProperty('--env-banner-height', '0px');
    document.documentElement.style.setProperty('--notice-top-offset', noticeTop);
    window.dispatchEvent(new Event('notice-top-offset-change'));
    return () => {
      document.documentElement.style.setProperty('--notice-top-offset', `${NOTICE_TOP_OFFSET_PX}px`);
      window.dispatchEvent(new Event('notice-top-offset-change'));
    };
  }, [tier]);

  if (!shouldShowEnvironmentBanner(tier)) return null;

  const isDev = tier === 'dev';

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: 'fixed',
        top: NOTICE_TOP_OFFSET_PX,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: theme.zIndex.tooltip + 1,
        height: ENV_BANNER_HEIGHT_PX,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        borderRadius: 999,
        backgroundColor: (muiTheme) => alpha(
          isDev ? muiTheme.palette.info.dark : muiTheme.palette.warning.dark,
          0.88,
        ),
        backdropFilter: 'blur(6px)',
        color: 'common.white',
        typography: 'caption',
        fontWeight: 700,
        letterSpacing: 0.08,
        textTransform: 'uppercase',
        boxShadow: 2,
        maxWidth: 'calc(100% - 24px)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {t(isDev ? 'environment.devBanner' : 'environment.testBanner')}
    </Box>
  );
}

export default EnvironmentBanner;
